import type { IProductImage, ResponsiveImageModel } from 'autogen/swagger/all';
import type { PictureProps } from 'components/generic/picture/shared/types';
import { createSrcSet } from 'components/pages/Contentful/components/Image/shared/helpers';
import { getCloudFlareProxiedUrl } from 'helpers/imageProxyHelper';
import type { TypeImageDetailed } from 'types/autogen/contentful';
import type { ImageProps } from '../shared/types';

function mapPicturePropsToImageProps(props: PictureProps): ImageProps {
    const { alt, sources, fallbackSrc, aspectRatio } = props;

    function mapPictureSourcePropsToSrcSet(sources: PictureProps['sources']): ImageProps['srcSet'] {
        return sources
            .map((source) => {
                const { src, width } = source;
                return `${src} ${width}w`;
            })
            .filter((entry) => entry.length > 0)
            .join(', ');
    }

    return {
        src: fallbackSrc || '',
        alt,
        srcSet: mapPictureSourcePropsToSrcSet(sources) || undefined,
        aspectRatio
    };
}

function mapResponsiveImageModelToImageProps(props: ResponsiveImageModel): ImageProps {
    const { altText, lazyLoad, sources, fallBackUrl, imageClass } = props;

    function mapResponsiveImageSourcesToSrcSet(sources: ResponsiveImageModel['sources']): ImageProps['srcSet'] {
        return sources
            .map((source) => {
                const { images } = source;
                return images
                    .filter((image) => image.url && image.width)
                    .map((image) => `${image.url} ${image.width}w`)
                    .join(', ');
            })
            .join(', ');
    }

    return {
        src: fallBackUrl || '',
        srcSet: mapResponsiveImageSourcesToSrcSet(sources) || undefined,
        alt: altText || '',
        loading: lazyLoad ? 'lazy' : undefined,
        className: imageClass
    };
}

function mapTypeImageDetailedToImageProps(props: TypeImageDetailed): ImageProps | null {
    const { altText, image, localizedImage } = props.fields;
    const imageAsset = localizedImage || image;

    if (!imageAsset) return null;
    const { fields: assetFields } = imageAsset;
    if (!assetFields?.file) return null;
    const { file } = assetFields;
    const {
        details: { image: imageFile },
        url
    } = file;

    return {
        src: getCloudFlareProxiedUrl(url),
        srcSet: createSrcSet(file),
        alt: altText || '',
        width: imageFile?.width,
        height: imageFile?.height
    };
}

export { mapPicturePropsToImageProps, mapResponsiveImageModelToImageProps, mapTypeImageDetailedToImageProps };
